<script setup>

import {computed, onMounted, onUnmounted, ref, watch} from "vue";
import { BarChart, PieChart } from 'vue-chart-3';
import 'chart.js/auto';
import ButtonWithConfirmation from "../../components/buttons/buttonWithConfirmation.vue";
import { useNotificationStore } from '@/stores/NotificationStore.js'
const notificationStore = useNotificationStore()

const mediaBankEntries = ref([])
const maxInitialId = ref(null)
const filter = ref('')
const inUseFilter = ref('')
const search = ref('')
const sortBy = ref('updated_at')
const sortOrder = ref('desc')
const loading = ref(false)
const loadingMore = ref(false)
const searchTimeout = ref(null)
const currentPage = ref(1)
const hasMorePages = ref(true)
const perPage = 10

// Storage stats
const storageStats = ref(null)
const loadingStats = ref(false)
const storageStatsExpanded = ref(false)

const showingItem = ref({
    id: null,
    type: null,
    title: null,
    inUse: true,
    duration: null,
    usages: [],
})

const lightboxItem = ref(null)
const showLightbox = ref(false)

const checkUsage = async (item) => {
    const res = await axios.get(route('mediaBank.entries.checkUsage', {id: item.id}))
    return res.data
}

const loadItems = async (reset = false) => {
    if (reset) {
        currentPage.value = 1
        mediaBankEntries.value = []
        hasMorePages.value = true
        loading.value = true
    } else {
        if (loadingMore.value || !hasMorePages.value) {
            return
        }
        loadingMore.value = true
    }

    const params = new URLSearchParams()
    if (search.value && search.value.trim() !== '') {
        params.append('search', search.value.trim())
    }
    if (filter.value && filter.value !== '') {
        params.append('type', filter.value)
    }
    if (inUseFilter.value && inUseFilter.value !== '') {
        params.append('in_use', inUseFilter.value)
    }
    if (sortBy.value) {
        params.append('sort_by', sortBy.value)
    }
    if (sortOrder.value) {
        params.append('sort_order', sortOrder.value)
    }
    params.append('page', currentPage.value)
    params.append('per_page', perPage)

    const queryString = params.toString()
    const url = route('mediaBank.entries.advanced-index') + (queryString ? '?' + queryString : '')

    try {
        const response = await axios.get(url);
        // Handle both paginated response (data.data) and direct collection
        const newEntries = response.data.data || response.data || []
        const pagination = response.data

        if (reset) {
            mediaBankEntries.value = newEntries
            // Track the maximum ID from initial load to identify truly new items
            if (maxInitialId.value === null && newEntries.length > 0) {
                maxInitialId.value = Math.max(...newEntries.map((entry) => entry.id))
            }
        } else {
            mediaBankEntries.value.push(...newEntries)
        }

        // Check if there are more pages
        // Handle both Laravel pagination format and simple array responses
        if (pagination.current_page !== undefined && pagination.last_page !== undefined) {
            hasMorePages.value = pagination.current_page < pagination.last_page
            if (pagination.current_page < pagination.last_page) {
                currentPage.value = pagination.current_page + 1
            } else {
                hasMorePages.value = false
            }
        } else if (pagination.next_page_url !== undefined) {
            // Handle pagination with next_page_url
            hasMorePages.value = pagination.next_page_url !== null
            if (pagination.next_page_url) {
                currentPage.value++
            }
        } else {
            // If no pagination info, check if we got fewer items than requested
            hasMorePages.value = newEntries.length >= perPage
            if (hasMorePages.value) {
                currentPage.value++
            }
        }
    } catch (error) {
        console.error('Error loading media bank entries:', error)
        if (reset) {
            mediaBankEntries.value = []
        }
        hasMorePages.value = false
    } finally {
        loading.value = false
        loadingMore.value = false
    }
}

const loadMore = () => {
    if (!loadingMore.value && hasMorePages.value && !loading.value) {
        loadItems(false)
    }
}

let scrollTimeout = null
const handleScroll = () => {
    // Throttle scroll events
    if (scrollTimeout) {
        return
    }

    scrollTimeout = setTimeout(() => {
        scrollTimeout = null

        const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop
        const windowHeight = window.innerHeight
        const documentHeight = Math.max(
            document.body.scrollHeight,
            document.body.offsetHeight,
            document.documentElement.clientHeight,
            document.documentElement.scrollHeight,
            document.documentElement.offsetHeight
        )

        // Load more when user is within 300px of the bottom
        const distanceFromBottom = documentHeight - (scrollTop + windowHeight)
        if (distanceFromBottom < 300) {
            loadMore()
        }
    }, 100)
}

const showItem = async (item) => {
    showingItem.value.id = item.id
    showingItem.value.type = item.type
    showingItem.value.title = item.title
    showingItem.value.duration = item.duration

    const usage = await checkUsage(item)
    showingItem.value.inUse = usage.in_use
    showingItem.value.usages = usage.usages
}

const openLightbox = (item) => {
    // For files, open in new tab
    if (item.type === 'file' && item.display_url) {
        window.open(item.display_url, '_blank')
        return
    }

    // For images, videos, and audio, open in lightbox
    lightboxItem.value = item
    showLightbox.value = true
}

const closeLightbox = () => {
    showLightbox.value = false
    lightboxItem.value = null
}

const closeItem = () => {
    showingItem.value = {
        id: null,
        type: null,
        title: null,
        inUse: true,
        duration: null,
        usages: [],
    }
}

const setFilter = (selectedFilter) => {
    filter.value = selectedFilter
    loadItems(true)
}

const setInUseFilter = (value) => {
    inUseFilter.value = value
    loadItems(true)
}

const setSortBy = (value) => {
    sortBy.value = value
    loadItems(true)
}

const setSortOrder = (value) => {
    sortOrder.value = value
    loadItems(true)
}

const filteredMediaBankEntries = computed(() => {
    return mediaBankEntries.value
})

const formatDuration = (seconds) => {
    if (!seconds) return ''
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    if (mins > 0) {
        return `${mins}:${secs.toString().padStart(2, '0')}`
    }
    return `${secs}s`
}

const loadStorageStats = async () => {
    loadingStats.value = true
    try {
        const url = route('mediaBank.entries.storageStats')
        const response = await axios.get(url)
        storageStats.value = response.data
    } catch (error) {
        console.error('Error loading storage stats:', error)
        if (error.response) {
            console.error('Response status:', error.response.status)
            console.error('Response data:', error.response.data)
        } else if (error.request) {
            console.error('No response received:', error.request)
        } else {
            console.error('Error message:', error.message)
        }
        // Don't set to null, keep previous value if available
    } finally {
        loadingStats.value = false
    }
}

const toggleStorageStats = () => {
    storageStatsExpanded.value = !storageStatsExpanded.value
}

const barChartData = computed(() => {
    if (!storageStats.value) return null

    return {
        labels: [
            _mft('mediaBank:storageStatistics.inUse'),
            _mft('mediaBank:storageStatistics.notInUse'),
            _mft('mediaBank:storageStatistics.available')
        ],
        datasets: [{
            label: _mft('mediaBank:storageStatistics'),
            data: [
                storageStats.value.in_use.bytes,
                storageStats.value.not_in_use.bytes,
                Math.max(0, storageStats.value.max_bytes - storageStats.value.total_bytes)
            ],
            backgroundColor: [
                '#475569', // Darker slate for in use - more contrast
                '#64748b', // Medium slate for not in use
                '#cbd5e1'  // Light slate for available - clear distinction
            ],
        }]
    }
})

const barChartOptions = computed(() => {
    return {
        responsive: true,
        maintainAspectRatio: true,
        aspectRatio: 4/3,
        devicePixelRatio: typeof window !== 'undefined' ? window.devicePixelRatio : 1,
        layout: {
            padding: {
                left: 15,
                right: 15,
                top: 15,
                bottom: 15
            }
        },
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                callbacks: {
                    label: function(context) {
                        const label = context.label || ''
                        const value = context.parsed.y
                        if (value >= 1073741824) {
                            return label + ': ' + (value / 1073741824).toFixed(2) + ' GB'
                        } else if (value >= 1048576) {
                            return label + ': ' + (value / 1048576).toFixed(2) + ' MB'
                        } else if (value >= 1024) {
                            return label + ': ' + (value / 1024).toFixed(2) + ' KB'
                        }
                        return label + ': ' + value + ' B'
                    }
                }
            }
        },
        scales: {
            x: {
                stacked: true,
                grid: {
                    display: true,
                    color: '#e5e7eb', // Subtle gray-200 for soft grid lines
                    lineWidth: 1
                },
                ticks: {
                    color: '#6b7280', // gray-500
                    font: {
                        size: 11
                    }
                }
            },
            y: {
                stacked: true,
                grid: {
                    display: true,
                    color: '#e5e7eb', // Subtle gray-200 for soft grid lines
                    lineWidth: 1
                },
                ticks: {
                    color: '#6b7280', // gray-500
                    font: {
                        size: 11
                    },
                    callback: function(value) {
                        if (value >= 1073741824) {
                            return (value / 1073741824).toFixed(1) + ' GB'
                        } else if (value >= 1048576) {
                            return (value / 1048576).toFixed(0) + ' MB'
                        }
                        return value
                    }
                }
            }
        }
    }
})

const pieChartData = computed(() => {
    if (!storageStats.value) return null

    const typeLabels = {
        image: _mft('mediaBank:storageStatistics.images'),
        video: _mft('mediaBank:storageStatistics.videos'),
        audio: _mft('mediaBank:storageStatistics.audio'),
        file: _mft('mediaBank:storageStatistics.files')
    }

    const typeOrder = ['image', 'video', 'audio', 'file']
    const typeColors = {
        image: '#3b82f6', // Blue - more distinct
        video: '#475569', // Dark slate - replacing red, more contrast
        audio: '#10b981', // Green - more distinct
        file: '#f59e0b', // Amber - more distinct
    }

    return {
        labels: typeOrder.map(type => typeLabels[type]),
        datasets: [{
            data: typeOrder.map(type => storageStats.value.by_type[type].bytes),
            backgroundColor: typeOrder.map(type => typeColors[type]),
        }]
    }
})

const pieChartOptions = computed(() => {
    if (!storageStats.value) return {}

    const typeOrder = ['image', 'video', 'audio', 'file']

    return {
        responsive: true,
        maintainAspectRatio: true,
        aspectRatio: 4/3,
        devicePixelRatio: typeof window !== 'undefined' ? window.devicePixelRatio : 1,
        plugins: {
            legend: {
                position: 'bottom',
                labels: {
                    color: '#374151',
                    padding: 12,
                    usePointStyle: true,
                    boxWidth: 12,
                    font: {
                        size: 11
                    }
                }
            },
            tooltip: {
                callbacks: {
                    label: function(context) {
                        const label = context.label || ''
                        const value = context.parsed
                        const typeIndex = context.dataIndex
                        const type = typeOrder[typeIndex]
                        const percentage = storageStats.value.by_type[type].percentage
                        if (value >= 1073741824) {
                            return `${label}: ${(value / 1073741824).toFixed(2)} GB (${percentage}%)`
                        } else if (value >= 1048576) {
                            return `${label}: ${(value / 1048576).toFixed(2)} MB (${percentage}%)`
                        } else if (value >= 1024) {
                            return `${label}: ${(value / 1024).toFixed(2)} KB (${percentage}%)`
                        }
                        return `${label}: ${value} B (${percentage}%)`
                    }
                }
            }
        },
        layout: {
            padding: {
                left: 15,
                right: 15,
                top: 10,
                bottom: 10
            }
        }
    }
})

// Debounce search
watch(search, () => {
    if (searchTimeout.value) {
        clearTimeout(searchTimeout.value)
    }
    searchTimeout.value = setTimeout(() => {
        loadItems(true)
    }, 300)
})

const handleKeydown = (event) => {
    if (event.key === 'Escape' && showLightbox.value) {
        closeLightbox()
    }
}

onMounted(() => {
    loadItems(true)
    loadStorageStats()
    window.addEventListener('scroll', handleScroll, { passive: true })
    // Also check on window resize in case content height changes
    window.addEventListener('resize', handleScroll, { passive: true })
    window.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
    window.removeEventListener('scroll', handleScroll)
    window.removeEventListener('resize', handleScroll)
    window.removeEventListener('keydown', handleKeydown)
    if (scrollTimeout) {
        clearTimeout(scrollTimeout)
    }
})

</script>
<template>
    <div>
        <frosted-bar>
            <div class="flex items-center justify-between space-x-4">
                <h1>{{ _mft('mediaBank:mediaLibrary') }}</h1>

                <div class="flex items-center space-x-4">

                </div>
            </div>
        </frosted-bar>

        <div class="mt-8 pb-24">
            <!-- Storage Statistics -->
            <div v-if="loadingStats" class="mb-8">
                <div class="bg-white rounded-wl shadow p-6">
                    <div class="text-center text-gray-400">
                        <i class="fa-solid fa-spinner fa-spin"></i>
                        <span class="ml-2">Laddar lagringsstatistik...</span>
                    </div>
                </div>
            </div>
            <div v-else-if="storageStats" class="mb-8">
                <div class="bg-white rounded-wl shadow">
                    <!-- Collapsed Header -->
                    <div
                        class="p-6 cursor-pointer rounded-wl hover:bg-gray-50 transition-colors flex items-center justify-between"
                        :class="storageStatsExpanded ? 'rounded-b-none' : ''"
                        @click="toggleStorageStats"
                    >
                        <div class="flex items-center space-x-4 flex-wrap gap-2">
                            <h2 class="text-lg font-semibold">{{ _mft('mediaBank:storageStatistics') }}</h2>
                            <div class="text-sm text-gray-600">
                                {{ _mft('mediaBank:storageStatistics.totalUsage') }}: {{ storageStats.total_formatted }} / {{ storageStats.max_formatted }}
                            </div>
                            <div class="text-sm font-medium text-gray-700">
                                {{ _mft('mediaBank:storageStatistics.usedTotal', { percentage: storageStats.percentage_used }) }}
                            </div>
                        </div>
                        <div class="flex-shrink-0 ml-4 flex items-center space-x-2">
                            <span class="text-sm text-gray-600">{{ _mft(storageStatsExpanded ? 'mediaBank:storageStatistics.hideDetails' : 'mediaBank:storageStatistics.showDetails') }}</span>
                            <i :class="storageStatsExpanded ? 'fa-chevron-up' : 'fa-chevron-down'" class="fa-solid text-gray-500"></i>
                        </div>
                    </div>

                    <!-- Expanded Content -->
                    <div v-if="storageStatsExpanded" class="px-6 pb-6 border-t border-gray-200">
                        <!-- Summary Text Section - Above Charts -->
                        <div class="mt-6 mb-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <h3 class="text-sm font-medium text-gray-700 mb-3">
                                    {{ _mft('mediaBank:storageStatistics.totalUsage') }}: {{ storageStats.total_formatted }} / {{ storageStats.max_formatted }}
                                </h3>
                                <div class="text-xs text-gray-500 space-y-2 bg-gray-50 p-4 rounded-wl">
                                    <div class="flex justify-between">
                                        <span>{{ _mft('mediaBank:storageStatistics.inUse') }}:</span>
                                        <span class="font-medium">{{ storageStats.in_use.formatted }} ({{ storageStats.in_use.percentage }}%)</span>
                                    </div>
                                    <div class="flex justify-between">
                                        <span>{{ _mft('mediaBank:storageStatistics.notInUse') }}:</span>
                                        <span class="font-medium">{{ storageStats.not_in_use.formatted }} ({{ storageStats.not_in_use.percentage }}%)</span>
                                    </div>
                                    <div class="flex justify-between">
                                        <span>{{ _mft('mediaBank:storageStatistics.available') }}:</span>
                                        <span class="font-medium">{{ storageStats.available.formatted }}</span>
                                    </div>
                                    <div class="text-center pt-2 mt-2 border-t border-gray-300">
                                        <span class="font-semibold text-gray-700">{{ _mft('mediaBank:storageStatistics.usedTotal', { percentage: storageStats.percentage_used }) }}</span>
                                    </div>
                                </div>
                            </div>
                            <div>
                                <h3 class="text-sm font-medium text-gray-700 mb-3">{{ _mft('mediaBank:storageStatistics.distributionByType') }}</h3>
                                <div class="text-xs text-gray-500 space-y-2 bg-gray-50 p-4 rounded-wl">
                                    <div class="flex justify-between">
                                        <span>{{ _mft('mediaBank:storageStatistics.images') }}:</span>
                                        <span class="font-medium">{{ storageStats.by_type.image.formatted }} ({{ storageStats.by_type.image.percentage }}%)</span>
                                    </div>
                                    <div class="flex justify-between">
                                        <span>{{ _mft('mediaBank:storageStatistics.videos') }}:</span>
                                        <span class="font-medium">{{ storageStats.by_type.video.formatted }} ({{ storageStats.by_type.video.percentage }}%)</span>
                                    </div>
                                    <div class="flex justify-between">
                                        <span>{{ _mft('mediaBank:storageStatistics.audio') }}:</span>
                                        <span class="font-medium">{{ storageStats.by_type.audio.formatted }} ({{ storageStats.by_type.audio.percentage }}%)</span>
                                    </div>
                                    <div class="flex justify-between">
                                        <span>{{ _mft('mediaBank:storageStatistics.files') }}:</span>
                                        <span class="font-medium">{{ storageStats.by_type.file.formatted }} ({{ storageStats.by_type.file.percentage }}%)</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Charts Section - Below Text -->
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <!-- Bar Chart: Total Usage -->
                            <div class="bg-white border border-gray-200 rounded-wl p-4 @container">
                                <div class="w-full aspect-[4/3]">
                                    <BarChart v-if="barChartData" :chart-data="barChartData" :options="barChartOptions" />
                                </div>
                            </div>

                            <!-- Pie Chart: Distribution by Type -->
                            <div class="bg-white border border-gray-200 rounded-wl p-4 @container">
                                <div class="w-full aspect-[4/3]">
                                    <PieChart v-if="pieChartData" :chart-data="pieChartData" :options="pieChartOptions" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <media-upload
                class="mx-auto max-w-4xl"
                :hide-on-completion="true"
                @upload-complete="loadItems(true); loadStorageStats()"
            ></media-upload>

            <div class="md:flex items-center justify-end @container space-x-2 flex-wrap gap-2">
                <button-bar
                    size="xs"
                    class="max-w-xs !mb-0"
                    :responsive="false"
                    :options="[
                        { label: _mft('mediaBank:filetype.all'), name: '' },
                        { label: _mft('mediaBank:filetype.image'), name: 'image' },
                        { label: _mft('mediaBank:filetype.video'), name: 'video' },
                        { label: _mft('mediaBank:filetype.sound'), name: 'audio' },
                        { label: _mft('mediaBank:filetype.file'), name: 'file' },
                    ]"
                    :selected-option-name="filter"
                    @select="setFilter($event.name)"
                />
                <button-bar
                    size="xs"
                    class="max-w-xs !mb-0"
                    :responsive="false"
                    :options="[
                        { label: _mft('mediaBank:filter.all'), name: '' },
                        { label: _mft('mediaBank:filter.inUse'), name: 'true' },
                        { label: _mft('mediaBank:filter.notInUse'), name: 'false' },
                    ]"
                    :selected-option-name="inUseFilter"
                    @select="setInUseFilter($event.name)"
                />
                <select
                    v-model="sortBy"
                    @change="loadItems(true)"
                    class="text-xs border rounded px-2 py-1"
                >
                    <option value="updated_at">{{ _mft('mediaBank:sort.lastUpdated') }}</option>
                    <option value="created_at">{{ _mft('mediaBank:sort.lastCreated') }}</option>
                    <option value="title">{{ _mft('mediaBank:sort.title') }}</option>
                    <option value="duration">{{ _mft('mediaBank:sort.duration') }}</option>
                    <option value="file_size">{{ _mft('mediaBank:sort.fileSize') }}</option>
                </select>
                <button
                    @click="setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')"
                    class="text-xs border rounded px-2 py-1"
                >
                    <i :class="sortOrder === 'asc' ? 'fa-arrow-up' : 'fa-arrow-down'" class="fa-solid"></i>
                </button>
                <input-text
                    class="!mb-0 mt-2 md:-mt-1"
                    input-class="text-xs"
                    :placeholder="_mft('shared:search')"
                    v-model:content="search"
                />
            </div>

            <list-basic>
                <list-basic-item
                    v-if="loading && mediaBankEntries.length === 0"
                >
                    <span class="text-gray-400">
                        Laddar...
                    </span>
                </list-basic-item>
                <list-basic-item
                    v-else-if="filteredMediaBankEntries.length === 0"
                >
                    <span class="text-gray-400">
                        {{ _mft('mediaBank:noMediaToShowHere') }}
                    </span>
                </list-basic-item>
                <template v-else>
                    <list-basic-item
                        v-for="(item, index) in filteredMediaBankEntries"
                        :key="item.id"
                        class="py-3 border-b border-gray-200 last:border-b-0"
                    >
                        <div class="flex items-center space-x-4">
                            <!-- Preview thumbnail -->
                            <div
                                class="flex-shrink-0 relative cursor-pointer"
                                style="width: 120px; height: 80px;"
                                @click="openLightbox(item)"
                            >
                                <div
                                    v-if="item.preview_url && (item.type === 'image' || item.type === 'video')"
                                    class="w-full h-full rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center transition-transform hover:scale-105"
                                >
                                    <img
                                        :src="item.preview_url"
                                        :alt="item.title"
                                        class="w-full h-full object-cover"
                                    />
                                    <div
                                        v-if="item.type === 'video'"
                                        class="absolute inset-0 flex items-center justify-center bg-black bg-opacity-20"
                                    >
                                        <i class="fa-solid fa-play text-white text-2xl"></i>
                                    </div>
                                    <span
                                        v-if="maxInitialId !== null && item.id > maxInitialId"
                                        class="absolute top-1 left-1 h-4 leading-4 px-1 bg-wlPrimary text-wlPrimaryContrast text-xxs rounded-md flex items-center opacity-90 z-10"
                                    >{{ _mft('mediaBank:new') }}</span>
                                </div>
                                <div
                                    v-else
                                    class="w-full h-full rounded-lg bg-gray-100 flex items-center justify-center text-gray-400 transition-transform hover:scale-105"
                                >
                                    <div class="text-center">
                                        <i v-if="item.type === 'image'" class="fa-regular fa-image text-2xl mb-1"></i>
                                        <i v-else-if="item.type === 'video'" class="fa-regular fa-video text-2xl mb-1"></i>
                                        <i v-else-if="item.type === 'audio'" class="fa-regular fa-volume text-2xl mb-1"></i>
                                        <i v-else class="fa-regular fa-file text-2xl mb-1"></i>
                                        <span
                                            v-if="maxInitialId !== null && item.id > maxInitialId"
                                            class="block text-xxs bg-wlPrimary text-wlPrimaryContrast px-1 rounded mt-1"
                                        >{{ _mft('mediaBank:new') }}</span>
                                    </div>
                                </div>
                            </div>

                            <!-- Title and info -->
                            <div class="flex-1 min-w-0 cursor-pointer" @click="openLightbox(item)">
                                <div class="font-medium text-gray-900 truncate flex items-center gap-2">
                                    {{ item.title }}
                                    <span
                                        v-if="item.is_public"
                                        class="ml-1 inline-flex flex-shrink-0 items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"
                                        title="This file is publicly accessible"
                                    >
                                        <i class="fa-regular fa-globe mr-1"></i>
                                        Public
                                    </span>
                                </div>
                                <div class="text-sm text-gray-500 mt-1 space-x-2">
                                    <span class="capitalize">{{ item.type }}</span>
                                    <span v-if="item.duration" class="ml-2">
                                        {{ formatDuration(item.duration) }}
                                    </span>
                                    <span v-if="item.file_size_formatted" class="ml-2">
                                        {{ item.file_size_formatted }}
                                    </span>
                                    <span v-if="item.dimensions_formatted" class="ml-2">
                                        {{ item.dimensions_formatted }}
                                    </span>
                                    <span v-if="item.usage_count !== undefined && item.usage_count > 0" class="ml-2">
                                        Används på {{ item.usage_count }} {{ item.usage_count === 1 ? 'sida' : 'sidor' }}
                                    </span>
                                </div>
                                <div v-if="item.type === 'video'" class="text-xs text-gray-400 mt-1 space-x-2">
                                    <span v-if="item.has_transcription" class="inline-flex items-center">
                                        <i class="fa-solid fa-closed-captioning mr-1"></i>
                                        Transkription
                                    </span>
                                    <span v-if="item.has_subtitles" class="inline-flex items-center">
                                        <i class="fa-solid fa-subtitles mr-1"></i>
                                        Undertexter
                                    </span>
                                </div>
                            </div>

                            <!-- Action button -->
                            <div class="flex-shrink-0">
                                <button-secondary
                                    size="sm"
                                    @click="showItem(item)"
                                >
                                    {{ _mft('shared:action.show') }}
                                </button-secondary>
                            </div>
                        </div>
                    </list-basic-item>
                    <list-basic-item
                        v-if="loadingMore"
                    >
                        <div class="flex items-center justify-center space-x-2 text-gray-400 py-4">
                            <i class="fa-solid fa-spinner fa-spin"></i>
                            <span>Laddar fler...</span>
                        </div>
                    </list-basic-item>
                    <list-basic-item
                        v-else-if="hasMorePages && filteredMediaBankEntries.length > 0"
                    >
                        <div class="text-center py-4">
                            <button-secondary
                                @click="loadMore"
                                size="sm"
                            >
                                Ladda fler
                            </button-secondary>
                        </div>
                    </list-basic-item>
                    <list-basic-item
                        v-else-if="!hasMorePages && filteredMediaBankEntries.length > 0"
                    >
                        <div class="text-center text-gray-400 py-4">
                            <span>{{ _mft('mediaBank:allItemsLoaded') }}</span>
                        </div>
                    </list-basic-item>
                </template>
            </list-basic>
        </div>

        <slideout-with-slots
            v-if="showingItem.type !== null"
            @close="closeItem"
        >
            <template #body>
                <media-bank-entries-manage
                    :entry="showingItem"
                    @reload="loadItems"
                    @close="closeItem"
                />

            </template>
        </slideout-with-slots>

        <!-- Lightbox -->
        <Teleport to="body">
            <div
                v-if="showLightbox && lightboxItem"
                class="fixed inset-0 z-modal-base bg-black bg-opacity-90 flex items-center justify-center p-4"
                @click.self="closeLightbox"
            >
                <button
                    @click="closeLightbox"
                    class="absolute top-4 right-4 z-modal text-white hover:text-gray-300 transition-colors"
                >
                    <i class="fa-solid fa-times text-3xl"></i>
                </button>

                <div class="relative max-w-7xl max-h-full w-full">
                    <!-- Image -->
                    <div v-if="lightboxItem.type === 'image' && lightboxItem.display_url" class="flex items-center justify-center">
                        <img
                            :src="lightboxItem.display_url"
                            :alt="lightboxItem.title"
                            class="max-w-full max-h-[90vh] object-contain rounded-lg"
                        />
                    </div>

                    <!-- Video -->
                    <div v-else-if="lightboxItem.type === 'video'" class="flex items-center justify-center">
                        <div class="w-full max-w-5xl aspect-video bg-black rounded-lg overflow-hidden">
                            <media-bank-entries-display
                                :data-url="route('mediaBank.entries.show', lightboxItem.id)"
                                class="w-full h-full"
                            />
                        </div>
                    </div>

                    <!-- Audio -->
                    <div v-else-if="lightboxItem.type === 'audio' && lightboxItem.display_url" class="flex items-center justify-center">
                        <div class="bg-white rounded-lg p-8 max-w-2xl w-full">
                            <h3 class="text-xl font-semibold mb-4 text-gray-900">{{ lightboxItem.title }}</h3>
                            <audio
                                :src="lightboxItem.display_url"
                                controls
                                autoplay
                                class="w-full"
                            ></audio>
                        </div>
                    </div>
                </div>
            </div>
        </Teleport>

    </div>
</template>
